import React, { useRef, useState, useEffect, useCallback } from "react";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import { Module, SubMenu } from "../../../models/menu/menu.model";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { getUrlPathFromComponentPath } from "../menu/routeMapping";
import "./ProfileMenu.css";
import AuthService from "../../../services/autentication/auth.service";
import RouteConstant from "../../../constants/route.constants";
import ProfilePictureService from "../../../services/profile-picture/profile-picture.service";
import ImageCropper from "../../common/ImageCropper";

interface ProfileMenuProps {
    currentUser: any;
    myAccountModule: Module | null;
    onLogout: () => void;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    // Quick actions (only shown in desktop view on mobile)
    onDownloadBroucher?: () => void;
    onShareWhatsApp?: () => void;
    onViewNews?: () => void;
    onCallExecutive?: () => void;
    isAdmin?: boolean;
    isUserLoggedIn?: boolean;
    isMobileDesktopView?: boolean;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({
    currentUser,
    myAccountModule,
    onLogout,
    isOpen: externalIsOpen,
    onOpenChange,
    onDownloadBroucher,
    onShareWhatsApp,
    onViewNews,
    onCallExecutive,
    isAdmin = false,
    isUserLoggedIn: isLoggedInProp = false,
    isMobileDesktopView = false,
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [logoutDialogVisible, setLogoutDialogVisible] = useState(false);
    const [profilePicUrl, setProfilePicUrl] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
    const [cropDialogVisible, setCropDialogVisible] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const toastRef = useRef<Toast>(null);
    const navigate = useNavigate();

    // Load profile picture from Azure Blob Storage
    const loadProfilePicture = useCallback(async () => {
        setProfilePicUrl(null);
        if (currentUser?.userID) {
            try {
                const url = await ProfilePictureService.getProfilePictureUrl(currentUser.userID);
                setProfilePicUrl(url);
            } catch (error) {
                setProfilePicUrl(null);
            }
        }
    }, [currentUser?.userID]);

    useEffect(() => {
        loadProfilePicture();
    }, [loadProfilePicture]);


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Supported image types
        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
            "image/webp",
            "image/heic",
            "image/heif",
        ];

        if (!allowedTypes.includes(file.type)) {
            // Some browsers might not report HEIC/HEIF MIME types correctly
            const extension = file.name.split(".").pop()?.toLowerCase();
            const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"];

            if (!extension || !allowedExtensions.includes(extension)) {
                toastRef.current?.show({
                    severity: "error",
                    summary: "Invalid File Type",
                    detail: "Please upload an image in JPG, PNG, GIF, WebP, or HEIC format.",
                    life: 5000,
                });
                if (fileInputRef.current) fileInputRef.current.value = "";
                return;
            }
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            setCropImageSrc(reader.result as string);
            setCropDialogVisible(true);
            if (fileInputRef.current) fileInputRef.current.value = "";
        };
        reader.readAsDataURL(file);
    };

    const handleCropComplete = async (blob: Blob) => {
        if (!currentUser?.userID) return;

        setIsUploading(true);

        try {
            const fileName = `${currentUser.userID}.webp`;
            const uploadFile = new File([blob], fileName, { type: 'image/webp' });

            await ProfilePictureService.uploadProfilePicture(uploadFile);
            await loadProfilePicture();

            toastRef.current?.show({
                severity: "success",
                summary: "Profile Picture Updated",
                detail: "Your profile picture has been updated successfully.",
                life: 3000,
            });

            // Close dialog ONLY on success
            setCropDialogVisible(false);
            setCropImageSrc(null);
        } catch (error) {
            console.error("[ProfileMenu] Upload failed:", error);
            toastRef.current?.show({
                severity: "error",
                summary: "Upload Failed",
                detail: typeof error === 'string' ? error : "Failed to upload profile picture.",
                life: 6000,
            });
        } finally {
            setIsUploading(false);
        }
    };

    // Use external state if provided, otherwise use internal state
    const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

    const setIsOpen = (value: boolean) => {
        if (onOpenChange) {
            onOpenChange(value);
        } else {
            setInternalIsOpen(value);
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    // Extract initials from name
    const getInitials = (name: string) => {
        if (!name) return "U";
        return name
            .split(" ")
            .map((n) => n[0])
            .slice(0, 2)
            .join("")
            .toUpperCase();
    };

    const handleMenuItemClick = (route: string | null, displayName: string) => {
        if (route) {
            const urlPath = getUrlPathFromComponentPath(route, displayName);
            navigate(urlPath);
            setIsOpen(false);
        }
    };

    // Render menu items recursively if needed, but for profile we usually flatten or show top level
    // The structure of myAccountModule usually has menus -> subMenus
    const renderAccountItems = () => {
        if (!myAccountModule || !myAccountModule.menus) return null;

        const items: JSX.Element[] = [];

        // Iterate through menus in the module
        myAccountModule.menus.forEach((menu) => {
            // If menu has no submenu but has a route, it's a direct link
            if ((!menu.subMenu || menu.subMenu.length === 0) && menu.route && menu.permissions.canView) {
                items.push(
                    <li
                        key={`menu-${menu.menuId}`}
                        className="profile-menu-item"
                        onClick={() => handleMenuItemClick(menu.route, menu.displayName)}
                    >
                        {menu.icon && <i className={menu.icon}></i>}
                        <span>{menu.displayName}</span>
                    </li>
                );
            }

            // If menu has submenus, add them
            if (menu.subMenu && menu.subMenu.length > 0) {
                menu.subMenu.forEach((subItem: SubMenu) => {
                    if (subItem.permissions.canView) {
                        items.push(
                            <li
                                key={`sub-${subItem.menuId}`}
                                className="profile-menu-item"
                                onClick={() => handleMenuItemClick(subItem.route, subItem.displayName)}
                            >
                                {subItem.icon && <i className={subItem.icon}></i>}
                                <span>{subItem.displayName}</span>
                            </li>
                        );
                    }
                });
            }
        });

        return items;
    };

    // Check if user is actually logged in (handles case where cache is cleared but UI is stale)
    const isUserAuthenticated = AuthService.isUserLoggedIn();

    return (
        <div className="profile-menu-container" ref={menuRef}>
            <Toast ref={toastRef} position="top-right" className="profile-toast" baseZIndex={10000} />
            <button
                className={`profile-trigger-btn ${isOpen ? "active" : ""}`}
                onClick={() => setIsOpen(!isOpen)}
                type="button"
                aria-label="Profile"
            >
                <div className={`profile-avatar ${profilePicUrl ? 'has-image' : ''}`}>
                    {profilePicUrl ? (
                        <img
                            src={profilePicUrl}
                            alt="Profile"
                            className="profile-image"
                            loading="eager"
                            decoding="async"
                            onError={() => {
                                setProfilePicUrl(null);
                            }}
                        />
                    ) : currentUser?.name ? (
                        getInitials(currentUser.name)
                    ) : (
                        <i className="pi pi-user"></i>
                    )}
                </div>
            </button>

            {isOpen && (
                <div className="profile-dropdown">
                    {!isUserAuthenticated ? (
                        /* Unauthenticated State - Show Login Button */
                        <div className="profile-dropdown-content p-3 flex flex-column align-items-center gap-3">
                            <i className="pi pi-exclamation-circle text-4xl text-yellow-500"></i>
                            <div className="text-center">
                                <span className="font-semibold block mb-1">Session Expired</span>
                                <span className="text-sm text-gray-600">Please login to continue</span>
                            </div>
                            <Button
                                label="Login"
                                icon="pi pi-sign-in"
                                className="w-full"
                                onClick={() => {
                                    setIsOpen(false);
                                    navigate(RouteConstant.login);
                                }}
                            />
                        </div>
                    ) : (
                        /* Authenticated State - Show Menu */
                        <>
                            <div className="profile-dropdown-header">
                                <div className="profile-avatar-wrapper">
                                    <div className={`profile-avatar dropdown-avatar ${profilePicUrl ? 'has-image' : ''}`}>
                                        {isUploading && (
                                            <div className="profile-upload-overlay">
                                                <i className="pi pi-spin pi-spinner"></i>
                                            </div>
                                        )}
                                        {profilePicUrl ? (
                                            <img
                                                src={profilePicUrl}
                                                alt="Profile"
                                                className="profile-image"
                                                loading="eager"
                                                decoding="async"
                                                onError={() => {
                                                    setProfilePicUrl(null);
                                                }}
                                            />
                                        ) : currentUser?.name ? (
                                            getInitials(currentUser.name)
                                        ) : (
                                            <i className="pi pi-user"></i>
                                        )}
                                    </div>
                                    <button
                                        className="profile-upload-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            fileInputRef.current?.click();
                                        }}
                                        title="Upload Profile Picture"
                                    >
                                        <i className="pi pi-camera"></i>
                                    </button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: "none" }}
                                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/heic,image/heif"
                                        onChange={handleFileChange}
                                    />
                                </div>
                                <div className="dropdown-user-info">
                                    <h3>{currentUser?.name || "User"}</h3>
                                    <span>{currentUser?.primaryContactNbr || ""}</span>
                                </div>
                            </div>

                            <ul className="profile-menu-list">
                                {renderAccountItems()}

                                {/* Quick actions (only shown in desktop view on mobile) */}
                                {(isMobileDesktopView || (typeof document !== 'undefined' && document.body.classList.contains('mobile-desktop-view'))) &&
                                    (onDownloadBroucher || onShareWhatsApp || onViewNews || onCallExecutive) && (
                                    <>
                                        <div className="profile-divider profile-mobile-actions-divider" />
                                        <div className="profile-mobile-actions">
                                            <span className="profile-mobile-actions-label">Quick Actions</span>
                                            <div className="profile-mobile-actions-grid">
                                                {onDownloadBroucher && (
                                                    <button className="profile-mobile-action-btn" onClick={() => { onDownloadBroucher(); setIsOpen(false); }}>
                                                        <i className="pi pi-download" />
                                                        <span>Download Broucher</span>
                                                    </button>
                                                )}
                                                {onShareWhatsApp && (
                                                    <button className="profile-mobile-action-btn whatsapp" onClick={() => { onShareWhatsApp(); setIsOpen(false); }}>
                                                        <i className="pi pi-whatsapp" />
                                                        <span>Share Broucher</span>
                                                    </button>
                                                )}
                                                {onViewNews && (
                                                    <button className="profile-mobile-action-btn" onClick={() => { onViewNews(); setIsOpen(false); }}>
                                                        <i className="pi pi-book" />
                                                        <span>News</span>
                                                    </button>
                                                )}
                                                {onCallExecutive && isLoggedInProp && (
                                                    <button className="profile-mobile-action-btn" onClick={() => { onCallExecutive(); setIsOpen(false); }}>
                                                        <i className="pi pi-phone" />
                                                        <span>Call Executive</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="profile-divider" />

                                <li
                                    className="profile-menu-item logout"
                                    onClick={() => {
                                        setIsOpen(false);
                                        setLogoutDialogVisible(true);
                                    }}
                                >
                                    <i className="pi pi-sign-out"></i>
                                    <span>Logout</span>
                                </li>
                            </ul>
                        </>
                    )}
                </div>
            )}

            {/* Image Crop Dialog */}
            <Dialog
                header="Crop Profile Picture"
                visible={cropDialogVisible}
                onHide={() => setCropDialogVisible(false)}
                style={{ width: '90vw', maxWidth: '520px', borderRadius: '16px' }}
                contentStyle={{ padding: '1.5rem 1.5rem 1rem' }}
                headerStyle={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #e8e8e8' }}
                breakpoints={{ '960px': '80vw', '641px': '92vw', '480px': '96vw' }}
                modal
                blockScroll
                dismissableMask={false}
            >
                {cropImageSrc && (
                    <ImageCropper
                        imageSrc={cropImageSrc}
                        onCropComplete={handleCropComplete}
                        onCancel={() => setCropDialogVisible(false)}
                        aspect={1}
                        loading={isUploading}
                    />
                )}
            </Dialog>

            {/* Logout Confirmation Dialog */}
            <Dialog
                visible={logoutDialogVisible}
                onHide={() => setLogoutDialogVisible(false)}
                modal
                showHeader={false}
                style={{ width: "90vw", maxWidth: "400px", borderRadius: "20px" }}
                contentStyle={{
                    padding: "clamp(1.5rem, 4vw, 2.5rem)",
                    textAlign: "center",
                    borderRadius: "20px",
                }}
                appendTo={document.body}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "clamp(1rem, 3vw, 1.5rem)",
                    }}
                >
                    {/* Icon */}
                    <div
                        style={{
                            width: "clamp(70px, 20vw, 100px)",
                            height: "clamp(70px, 20vw, 100px)",
                            borderRadius: "50%",
                            background: "#FFEBEE",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <i
                            className="pi pi-sign-out"
                            style={{
                                fontSize: "clamp(2rem, 6vw, 3rem)",
                                color: "#D32F2F",
                            }}
                        ></i>
                    </div>

                    {/* Title */}
                    <h2
                        style={{
                            margin: 0,
                            fontSize: "clamp(1.25rem, 4vw, 1.75rem)",
                            fontWeight: 600,
                            color: "#37474F",
                        }}
                    >
                        Confirm Logout
                    </h2>

                    {/* Message */}
                    <p
                        style={{
                            margin: 0,
                            fontSize: "clamp(0.875rem, 3vw, 1rem)",
                            color: "#37474F",
                            lineHeight: "1.6",
                        }}
                    >
                        Are you sure you want to logout?
                        <br />
                        You will need to login again to access your account.
                    </p>

                    {/* Buttons */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: window.innerWidth < 400 ? "column" : "row",
                            gap: "1rem",
                            width: "100%",
                            marginTop: "0.5rem",
                        }}
                    >
                        <Button
                            label="Cancel"
                            onClick={() => setLogoutDialogVisible(false)}
                            className="p-button-outlined"
                            style={{
                                flex: 1,
                                padding: "clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)",
                                fontSize: "clamp(0.875rem, 3vw, 1rem)",
                                fontWeight: 500,
                                borderRadius: "25px",
                                borderColor: "#B0BEC5",
                                color: "#546E7A",
                            }}
                        />
                        <Button
                            label="Logout"
                            onClick={() => {
                                setLogoutDialogVisible(false);
                                onLogout();
                            }}
                            style={{
                                flex: 1,
                                padding: "clamp(0.5rem, 2vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)",
                                fontSize: "clamp(0.875rem, 3vw, 1rem)",
                                fontWeight: 500,
                                borderRadius: "25px",
                                background: "#D32F2F",
                                border: "none",
                                color: "white",
                            }}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ProfileMenu;